/**
 * Service for scaling recipe ingredients based on portion changes
 */

interface ParsedIngredient {
  original: string
  quantity?: number
  unit?: string
  ingredient: string
  scalable: boolean
}

export class IngredientScaler {
  // Common Dutch units and their variations
  private static readonly DUTCH_UNITS = {
    // Weight
    'g': ['g', 'gram', 'gramme'],
    'kg': ['kg', 'kilo', 'kilogram'],
    
    // Volume
    'ml': ['ml', 'milliliter'],
    'l': ['l', 'liter', 'litre'],
    'dl': ['dl', 'deciliter'],
    'cl': ['cl', 'centiliter'],
    
    // Kitchen measures
    'el': ['el', 'eetlepel', 'eetlepels', 'grote lepel'],
    'tl': ['tl', 'theelepel', 'theelepels', 'kleine lepel'],
    'kop': ['kop', 'kops', 'kopje', 'kopjes'],
    'glas': ['glas', 'glazen'],
    'bak': ['bak', 'bakje', 'bakjes'],
    
    // Pieces
    'stuks': ['stuk', 'stuks', 'st'],
    'plak': ['plak', 'plakken', 'plakje', 'plakjes'],
    'teen': ['teen', 'tenen'],
    'takje': ['takje', 'takjes'],
    'bosje': ['bosje', 'bosjes'],
    'snuf': ['snuf', 'snufje'],
    'mespunt': ['mespunt', 'mespuntje'],
  }

  /**
   * Parse a Dutch ingredient string to extract quantity, unit, and ingredient name
   */
  static parseIngredient(ingredient: string): ParsedIngredient {
    const trimmed = ingredient.trim()
    
    // Try to match patterns like "200g flour", "2 eggs", "1 tbsp sugar"
    const patterns = [
      // "200 g bloem" or "200g bloem"
      /^(\d+(?:[.,]\d+)?)\s*([a-zA-Z]+)\s+(.+)$/,
      // "2 eieren" (number + ingredient)
      /^(\d+(?:[.,]\d+)?)\s+(.+)$/,
      // "een snufje zout" (word number)
      /^(een|twee|drie|vier|vijf|zes|zeven|acht|negen|tien)\s+(.+)$/i,
    ]

    for (const pattern of patterns) {
      const match = trimmed.match(pattern)
      if (match) {
        const [, quantityStr, unitOrIngredient, restOfIngredient] = match
        
        // Convert word numbers to digits
        const quantity = this.parseQuantity(quantityStr)
        
        if (restOfIngredient) {
          // Pattern 1: quantity + unit + ingredient
          const unit = this.normalizeUnit(unitOrIngredient)
          return {
            original: ingredient,
            quantity,
            unit,
            ingredient: restOfIngredient.trim(),
            scalable: true
          }
        } else {
          // Pattern 2 or 3: quantity + ingredient (no explicit unit, assume "stuks")
          return {
            original: ingredient,
            quantity,
            unit: 'stuks',
            ingredient: unitOrIngredient.trim(),
            scalable: true
          }
        }
      }
    }

    // If no quantity pattern found, check if it starts with common scalable words
    const scalableWords = ['snuf', 'mespunt', 'beetje', 'scheutje', 'handje']
    const isScalable = scalableWords.some(word => 
      trimmed.toLowerCase().includes(word)
    )

    return {
      original: ingredient,
      quantity: undefined,
      unit: undefined,
      ingredient: trimmed,
      scalable: isScalable
    }
  }

  /**
   * Convert Dutch word numbers to numeric values
   */
  private static parseQuantity(quantityStr: string): number {
    const wordToNumber: { [key: string]: number } = {
      'een': 1, 'twee': 2, 'drie': 3, 'vier': 4, 'vijf': 5,
      'zes': 6, 'zeven': 7, 'acht': 8, 'negen': 9, 'tien': 10
    }

    const lowerQuantity = quantityStr.toLowerCase()
    if (wordToNumber[lowerQuantity]) {
      return wordToNumber[lowerQuantity]
    }

    // Parse decimal numbers (handle both comma and period)
    return parseFloat(quantityStr.replace(',', '.'))
  }

  /**
   * Normalize unit names to standard forms
   */
  private static normalizeUnit(unit: string): string {
    const lowerUnit = unit.toLowerCase()
    
    for (const [standard, variations] of Object.entries(this.DUTCH_UNITS)) {
      if (variations.includes(lowerUnit)) {
        return standard
      }
    }
    
    return lowerUnit
  }

  /**
   * Scale ingredients for a different number of servings
   */
  static scaleIngredients(
    ingredients: string[], 
    originalServings: number, 
    newServings: number
  ): string[] {
    if (originalServings <= 0 || newServings <= 0) {
      return ingredients
    }

    const scaleFactor = newServings / originalServings

    return ingredients.map(ingredient => {
      const parsed = this.parseIngredient(ingredient)
      
      if (!parsed.scalable || parsed.quantity === undefined) {
        return ingredient
      }

      const scaledQuantity = parsed.quantity * scaleFactor
      const formattedQuantity = this.formatQuantity(scaledQuantity)
      
      if (parsed.unit) {
        return `${formattedQuantity} ${parsed.unit} ${parsed.ingredient}`
      } else {
        return `${formattedQuantity} ${parsed.ingredient}`
      }
    })
  }

  /**
   * Format quantity for display (avoid unnecessary decimals)
   */
  private static formatQuantity(quantity: number): string {
    // Round to 2 decimal places and remove trailing zeros
    const rounded = Math.round(quantity * 100) / 100
    
    // If it's a whole number, show as integer
    if (rounded % 1 === 0) {
      return rounded.toString()
    }
    
    // For fractions, show with minimal decimal places
    return rounded.toString()
  }

  /**
   * Get scaling suggestions based on common serving sizes
   */
  static getScalingSuggestions(originalServings: number): number[] {
    const suggestions = [1, 2, 4, 6, 8, 10, 12]
    return suggestions.filter(s => s !== originalServings).sort((a, b) => a - b)
  }
}

export default IngredientScaler
